import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, LOCALE_ID, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { LoginFacade, NotificationFacade, RecoverPasswordFacade } from '@ridango/components';
import { ResetCompleteEvent } from '@ridango/core';
import { passwordValidator } from '@ridango/core/lib/validators/password.validator';

import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'rwidget-reset-password',
    templateUrl: './reset-password.component.html'
})
export class ResetPasswordComponent implements OnInit {
    private componentDestroyed$ = new Subject<void>();

    loading$: Observable<boolean> = this.recoverPassword.loading$;
    token: string;
    passwordForm: FormGroup;
    showPassword = false;
    showPasswordRepeat = false;

    @Input() ondemandRequest = false;
    @Input() callback: string = null;

    @Output() onResetComplete = new EventEmitter<ResetCompleteEvent>();

    constructor(
        private notification: NotificationFacade,
        private route: ActivatedRoute,
        private recoverPassword: RecoverPasswordFacade,
        private loginFacade: LoginFacade,
        @Inject(LOCALE_ID) public locale: string,
        private cdr: ChangeDetectorRef,
        @Inject('ENV') public env: Env,
        private translate: TranslateService
    ) {}

    ngOnInit() {
        this.passwordForm = new FormGroup({
            password: new FormControl('', passwordValidator),
            passwordRepeat: new FormControl('', passwordValidator)
        });

        // clear login state
        this.loginFacade.logout(false);

        this.token = this.route.snapshot.params.token;
        this.recoverPassword.setRecoveryStep(3);

        // listen to state for error
        this.recoverPassword.error$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                (error) => error && this.notification.error(this.translate.instant('LOCAL_CHANGE_PASSWORD_FAILED'))
            );

        // listen to state for password change done
        this.recoverPassword.done$.pipe(takeUntil(this.componentDestroyed$)).subscribe((done) => {
            if (!done) {
                return;
            }

            const loginWhenResetPassword = this.env.REGISTRATION?.LOGIN_WHEN_RESET_PASSWORD ?? true;

            if (loginWhenResetPassword) {
                return this.handleSuccess();
            }

            return this.onResetComplete.emit({ loginSuccessful: true, authAccessToken: null });
        });
    }

    togglePassword() {
        this.showPassword = !this.showPassword;
        this.cdr.detectChanges();
    }

    togglePasswordRepeat() {
        this.showPasswordRepeat = !this.showPasswordRepeat;
        this.cdr.detectChanges();
    }

    focusHandler() {
        this.cdr.detectChanges();
    }

    submit() {
        if (this.passwordForm.valid) {
            if (this.passwordForm.controls['password'].value === this.passwordForm.controls['passwordRepeat'].value) {
                const password = this.passwordForm.value.password;
                this.recoverPassword.setNewPassword(password, this.token, this.locale, this.ondemandRequest);
            } else {
                this.notification.error(this.translate.instant('LOCAL_PASSWORDS_MUST_MATCH'));
            }
        } else {
            this.passwordForm.controls.password.markAsTouched();
            this.passwordForm.controls.passwordRepeat.markAsTouched();
        }
    }

    /**
     * returns the login state since login is attempted after password change
     * is succesful
     */
    handleSuccess() {
        if (this.callback) {
            this.recoverPassword.authAccessToken$
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    (token) => token && this.onResetComplete.emit({ loginSuccessful: true, authAccessToken: token })
                );
        } else {
            this.loginFacade.isAuthenticated$
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    (isAuthenticated) =>
                        isAuthenticated && this.onResetComplete.emit({ loginSuccessful: true, authAccessToken: null })
                );
        }

        this.loginFacade.error$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                (error) => error && this.onResetComplete.emit({ loginSuccessful: false, authAccessToken: null })
            );
    }

    getFirstError(control: AbstractControl, errorCodes: string[]): string {
        return errorCodes.find((errorCode) => control.hasError(errorCode));
    }
}
